import { HttpContext, HttpContextToken, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { catchError, EMPTY, noop, Observable, of } from 'rxjs';
import {
  InformationMessage,
  MessageContent,
  MessageContentType,
  MessageReply,
  TranslateParams,
} from '../interfaces/dialog.interface';
import { ClientErrorLink, ClientErrorResponse } from '../swagger-api';
import { isSatisfyUnion, MaybeUndefined } from '../utils';

interface AuthContext {
  email: string;
}

// BLOCKING UI - modal, NON BLOCKING UI - toast
export type ExceptionLevel = 'BLOCKING' | 'NON_BLOCKING';

export interface ExceptionSummary {
  action: () => void;
  level: ExceptionLevel;
  messsage: InformationMessage;
}

export const AUTH_CONTEXT = new HttpContextToken<AuthContext>(() => ({ email: '' }));
export const FILE_DOWNLOAD_CONTEXT = new HttpContextToken<boolean>(() => false);

export const authContext = (email: string) => new HttpContext().set(AUTH_CONTEXT, { email });
export const fileDownloadContext = (value: boolean) => new HttpContext().set(FILE_DOWNLOAD_CONTEXT, value);

export const SKIP_HANDLING = new HttpContextToken<boolean>(() => false);
export const skipHandling = (value: boolean) => new HttpContext().set(SKIP_HANDLING, value);

export class ErrorResponsesTexts {
  public static SESSION_EXPIRED_TITLE_HINT = 'SESSION_EXPIRED_TITLE_HINT';
  public static SESSION_EXPIRED_DESCRIPTION_HINT = 'SESSION_EXPIRED_DESCRIPTION_HINT';
  public static BAD_STATUS_CODE_TITLE_HINT = 'BAD_STATUS_CODE_TITLE_HINT';
  public static BAD_STATUS_CODE_DESCRIPTION_HINT = 'BAD_STATUS_CODE_DESCRIPTION_HINT';
  public static ERROR_FILE_DOWNLOAD_TITLE = 'ERROR_FILE_DOWNLOAD_TITLE';
  public static ERROR_FILE_DOWNLOAD_DESCRIPTION = 'ERROR_FILE_DOWNLOAD_DESCRIPTION';
  public static EMAIL_VERIFICATION_FAILED_TITLE_HINT = 'EMAIL_VERIFICATION_FAILED_TITLE_HINT';
  public static EMAIL_VERIFICATION_FAILED_DESCRIPTION_HINT = 'EMAIL_VERIFICATION_FAILED_DESCRIPTION_HINT';

  //  texts for compating
  //  error response texts always are in English
  public static PAGE_CANNOT_BE_OPENED_TITLE = 'Page cannot be opened';
  public static EMAIL_NOT_VERIFIED_ERROR_TITLE = 'Email not verified';
  public static EMAIL_VERIFICATION_LINK_EXPIRED = 'Email verification link expired';
  public static CANNOT_RESET_PASSWORD = 'Cannot reset password';
  public static CANNOT_RESET_PASSWORD_DESCRIPTION =
    "You've recently updated your email address but haven't confirmed it yet. Password reset is only available for the new email address associated with your account.<br><br>Confirm your new email to proceed.";
  public static EMAIL_VERIFICATION_FAILED_TITLE = 'Email verification failed';
  public static CHANGED_EMAIL_CONFIRMATION_REQUIRED_TITLE = 'Email confirmation required';
  public static ACCOUNT_DEACTIVATED_DESCRIPTION =
    'Access to your account has been disabled for violation of our terms. You may send a request to restore the access.';

  public static ADMIN_EMAIL_ALREADY_TAKEN_DESCRIPTION =
    'The specified email is already used for a registered account. Please contact us at %link[0]% to fix this issue.';

  public static ACCOUNT_UNDECIDED_DESCRIPTION = 'Your request to restore the account is under consideration.';
  public static ACCOUNT_DISABLED_DESCRIPTION =
    'Access to your account has been disabled for violation of our terms. If you believe your account is disabled in error, please contact us at %link[0]%.';
}

export abstract class BaseAPIException {
  protected level: ExceptionLevel = 'NON_BLOCKING';
  protected httpContext: HttpContext = new HttpContext();

  constructor(private exceptionCallback: () => void = () => void 0) {}

  public summarize(error: ClientErrorResponse, context: HttpContext): ExceptionSummary {
    this.httpContext = context;

    return {
      level: this.level,
      messsage: this.messsage(error),
      action: () => this.exceptionCallback(),
    };
  }

  private messsage: (error: ClientErrorResponse) => InformationMessage = (error) => ({
    level: 'ERROR',
    title: this.title(error),
    content: this.content(error),
    replies: this.actions(),
  });

  public abstract match: (status: HttpStatusCode, error: ClientErrorResponse, context: HttpContext) => boolean;

  protected abstract title: (error: ClientErrorResponse) => string;
  protected abstract content: (error: ClientErrorResponse) => MessageContent[];
  protected abstract actions: () => MessageReply[];
}

export class CommonException extends BaseAPIException {
  public override match = (_status: HttpStatusCode, _error: ClientErrorResponse) => true;

  protected override title = (error: ClientErrorResponse) => error.title;
  protected override content = (error: ClientErrorResponse) => buildMessageContent(error.description, error.links);
  protected override actions = () => [];
}

export class UnknownException extends BaseAPIException {
  public override match = (status: HttpStatusCode, _error: ClientErrorResponse) => !status;

  protected override title = (_error: ClientErrorResponse) => ErrorResponsesTexts.BAD_STATUS_CODE_TITLE_HINT;
  protected override content = (_error: ClientErrorResponse) =>
    buildMessageContent(ErrorResponsesTexts.BAD_STATUS_CODE_DESCRIPTION_HINT);
  protected override actions = () => [];
}

export class UnauthorizedException extends BaseAPIException {
  constructor() {
    super();
    this.level = 'BLOCKING';
  }

  public override match = (status: HttpStatusCode) => status === HttpStatusCode.Unauthorized;

  protected override title = (error: ClientErrorResponse) => error.title;
  protected override content = (error: ClientErrorResponse) => buildMessageContent(error.description, error.links);
  protected override actions = () => [];
}

export class EmailVerificationLinkExpired extends BaseAPIException {
  constructor(private resendEmailCallback: (email: string) => void) {
    super();
    this.level = 'BLOCKING';
  }

  public override match = (status: HttpStatusCode, error: ClientErrorResponse) =>
    status === HttpStatusCode.BadRequest && error.title === ErrorResponsesTexts.EMAIL_VERIFICATION_LINK_EXPIRED;

  private get context(): AuthContext {
    return this.httpContext.get(AUTH_CONTEXT);
  }

  protected override title = (error: ClientErrorResponse) => error.title;
  protected override content = (error: ClientErrorResponse) => buildMessageContent(error.description, error.links);
  protected override actions = () => [
    { label: 'BTN_CLOSE_HINT', callback: () => noop },
    { label: 'RESEND_VERIFICATION_EMAIL_BTN_HINT', callback: () => this.resendEmailCallback(this.context.email) },
  ];
}

export class PasswordRecoveryProhibited extends BaseAPIException {
  constructor(
    private resendEmailCallback: (email: string) => void,
    private navigateToLinkLogin: () => void,
  ) {
    super();
    this.level = 'BLOCKING';
  }

  public override match = (status: HttpStatusCode, error: ClientErrorResponse) =>
    status === HttpStatusCode.BadRequest &&
    error.title === ErrorResponsesTexts.CANNOT_RESET_PASSWORD &&
    error.description === ErrorResponsesTexts.CANNOT_RESET_PASSWORD_DESCRIPTION;

  private get context(): AuthContext {
    return this.httpContext.get(AUTH_CONTEXT);
  }

  protected override title = (error: ClientErrorResponse) => error.title;
  protected override content = (error: ClientErrorResponse) => buildMessageContent(error.description, error.links);
  protected override actions = () => [
    { label: 'BTN_CLOSE_HINT', callback: () => noop },
    {
      label: 'RESEND_VERIFICATION_EMAIL_BTN_HINT',
      callback: () => {
        this.resendEmailCallback(this.context.email);
        this.navigateToLinkLogin();
      },
    },
  ];
}

export class EmailVerificationFailed extends BaseAPIException {
  public override match = (status: HttpStatusCode, error: ClientErrorResponse) =>
    status === HttpStatusCode.BadRequest && error.title === ErrorResponsesTexts.EMAIL_VERIFICATION_FAILED_TITLE;

  private get context(): AuthContext {
    return this.httpContext.get(AUTH_CONTEXT);
  }

  protected override title = (_error: ClientErrorResponse) => ErrorResponsesTexts.EMAIL_VERIFICATION_FAILED_TITLE_HINT;
  protected override content = () =>
    buildMessageContent(ErrorResponsesTexts.EMAIL_VERIFICATION_FAILED_DESCRIPTION_HINT, [], {
      email: this.context.email,
    });

  protected override actions = () => [];
}

export class EmailNotVerified extends BaseAPIException {
  constructor(private resendEmailCallback: (email: string) => void) {
    super();
    this.level = 'BLOCKING';
  }

  public override match = (status: HttpStatusCode, error: ClientErrorResponse) =>
    status === HttpStatusCode.BadRequest && error.title === ErrorResponsesTexts.EMAIL_NOT_VERIFIED_ERROR_TITLE;

  private get context(): AuthContext {
    return this.httpContext.get(AUTH_CONTEXT);
  }

  protected override title = (error: ClientErrorResponse) => error.title;
  protected override content = (error: ClientErrorResponse) =>
    buildMessageContent(error.description, error.links, { email: this.context.email });

  protected override actions = () => [
    { label: 'BTN_CLOSE_HINT', callback: () => noop },
    { label: 'RESEND_VERIFICATION_EMAIL_BTN_HINT', callback: () => this.resendEmailCallback(this.context.email) },
  ];
}

export class ChangedEmailIsNotVerified extends BaseAPIException {
  constructor(private resendEmailChangedVerification: (email: string) => void) {
    super();
    this.level = 'BLOCKING';
  }

  public override match = (status: HttpStatusCode, error: ClientErrorResponse) =>
    status === HttpStatusCode.BadRequest &&
    error.title === ErrorResponsesTexts.CHANGED_EMAIL_CONFIRMATION_REQUIRED_TITLE;

  private get context(): AuthContext {
    return this.httpContext.get(AUTH_CONTEXT);
  }

  protected override title = (error: ClientErrorResponse) => error.title;
  protected override content = (error: ClientErrorResponse) =>
    buildMessageContent(error.description, [], { email: this.context.email });

  protected override actions = () => [
    { label: 'BTN_CLOSE_HINT', callback: () => noop },
    {
      label: 'RESEND_VERIFICATION_EMAIL_BTN_HINT',
      callback: () => this.resendEmailChangedVerification(this.context.email),
    },
  ];
}

export class FileNotDownload extends BaseAPIException {
  public override match = (status: HttpStatusCode, _error: ClientErrorResponse, context: HttpContext) =>
    ![HttpStatusCode.NotFound, HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden].includes(status) &&
    context.get(FILE_DOWNLOAD_CONTEXT);

  protected override title = (_error: ClientErrorResponse) => ErrorResponsesTexts.ERROR_FILE_DOWNLOAD_TITLE;
  protected override content = (_error: ClientErrorResponse) =>
    buildMessageContent(ErrorResponsesTexts.ERROR_FILE_DOWNLOAD_DESCRIPTION);
  protected override actions = () => [];
}

export class NotFoundException extends BaseAPIException {
  constructor(exceptionCallback: () => void) {
    super(exceptionCallback);
  }

  public override match = (status: HttpStatusCode, error: ClientErrorResponse) =>
    status === HttpStatusCode.NotFound && error.title === ErrorResponsesTexts.PAGE_CANNOT_BE_OPENED_TITLE;

  protected override title = (error: ClientErrorResponse) => error.title;
  protected override content = (error: ClientErrorResponse) => buildMessageContent(error.description, error.links);
  protected override actions = () => [];
}

export class AccountDeactivated extends BaseAPIException {
  constructor(
    private reactivatePetitionCallback: (email: string) => void,
    private navigateToLinkTerms: () => void,
  ) {
    super();
    this.level = 'BLOCKING';
  }

  public override match = (status: HttpStatusCode, error: ClientErrorResponse) =>
    status === HttpStatusCode.Unauthorized && error.description === ErrorResponsesTexts.ACCOUNT_DEACTIVATED_DESCRIPTION;

  private get context(): AuthContext {
    return this.httpContext.get(AUTH_CONTEXT);
  }

  protected override title = (error: ClientErrorResponse) => error.title;
  protected override content = (error: ClientErrorResponse) => buildMessageContent(error.description, error.links);
  protected override actions = () => [
    { label: 'BTN_LEARN_REQUEST_HINT', callback: () => this.navigateToLinkTerms() },
    { label: 'BTN_SEND_REQUEST_HINT', callback: () => this.reactivatePetitionCallback(this.context.email) },
  ];
}

export class AccountUndecidedException extends BaseAPIException {
  public override match = (status: HttpStatusCode, error: ClientErrorResponse) => {
    return (
      status === HttpStatusCode.Unauthorized && error.description === ErrorResponsesTexts.ACCOUNT_UNDECIDED_DESCRIPTION
    );
  };

  protected override title = (error: ClientErrorResponse) => error.title;
  protected override content = (error: ClientErrorResponse) => buildMessageContent(error.description, error.links);
  protected override actions = () => [];
}

export class AccountDisabledException extends BaseAPIException {
  public override match = (status: HttpStatusCode, error: ClientErrorResponse) => {
    return (
      status === HttpStatusCode.Unauthorized && error.description === ErrorResponsesTexts.ACCOUNT_DISABLED_DESCRIPTION
    );
  };

  protected override title = (error: ClientErrorResponse) => error.title;
  protected override content = (error: ClientErrorResponse) => buildMessageContent(error.description, error.links);
  protected override actions = () => [];
}

export class AdminEmailAlreadyTaken extends BaseAPIException {
  public override match = (status: HttpStatusCode, error: ClientErrorResponse) => {
    return (
      status === HttpStatusCode.BadRequest &&
      error.description === ErrorResponsesTexts.ADMIN_EMAIL_ALREADY_TAKEN_DESCRIPTION
    );
  };

  protected override title = (error: ClientErrorResponse) => error.title;
  protected override content = (error: ClientErrorResponse) => buildMessageContent(error.description, error.links);
  protected override actions = () => [];
}

const buildMessageContent = (
  description: string,
  links: ClientErrorLink[] = [],
  translateParams?: TranslateParams,
): MessageContent[] => {
  const hasLinks: (str: string) => boolean = (substring: string) =>
    Boolean(links.length) && substring.startsWith('link') && substring.indexOf('[') > -1 && substring.endsWith(']');

  const index: (substring: string) => number | never = (substring: string) => {
    const index = parseInt(substring.substring(substring.indexOf('[') + 1, substring.indexOf(']')));

    if (!Number.isInteger(index) || index < 0) {
      throw new Error('The link should contains index');
    }

    return index;
  };

  const content: (description: string, params?: TranslateParams) => MessageContent[] = (
    description: string,
    params?: TranslateParams,
  ) =>
    description
      .split('%')
      .map((str: string) => {
        const errorLink: MaybeUndefined<ClientErrorLink> = hasLinks(str) ? links.at(index(str)) : undefined;

        return !errorLink
          ? { type: 'text', value: str, translateParams: params }
          : { type: errorLink.type, value: errorLink.text };
      })
      .map((item: { type: string; value: string; translateParams?: TranslateParams }) => {
        const itemType: string = item.type;

        let type: MessageContentType = 'text';

        if (isSatisfyUnion<MessageContentType>(itemType, ['email', 'signup', 'text'])) {
          type = itemType;
        }

        return {
          type,
          value: item.value,
          translateParams: item.translateParams,
        };
      });

  try {
    return content(description, translateParams);
  } catch (e: unknown) {
    console.error(e);
  }

  return [{ type: 'text', value: '' }];
};

export const extractError = (error: object): ClientErrorResponse => {
  type ErrorResponse = {
    title: string;
    type: string;
    status: number;
    detail: { title: string; description: string };
  };

  type SwaggerValidationError = {
    title: string;
    type: string;
    status: number;
    detail: string;
  };

  type APIError = ClientErrorResponse | SwaggerValidationError | ErrorResponse;

  const isClientError = (error: APIError | object): error is ClientErrorResponse =>
    'title' in error && 'description' in error;

  const isSwaggerValidationError = (error: APIError | object): error is SwaggerValidationError =>
    'detail' in error && typeof error.detail === 'string';

  const isCustomError = (error: APIError | object): error is ErrorResponse =>
    'detail' in error && typeof error.detail === 'object' && 'title' in error.detail && 'description' in error.detail;

  if (isClientError(error)) {
    return {
      title: error.title,
      description: error.description,
      links: error.links ?? [],
    };
  }

  if (isSwaggerValidationError(error)) {
    return {
      title: error.title,
      description: error.detail,
      links: [],
    };
  }

  if (isCustomError(error)) {
    return {
      title: error.detail.title,
      description: error.detail.description,
      links: [],
    };
  }

  return { title: '', description: '', links: [] };
};

export function withReplaceBadRequest<T>(fallbackValue: T): (source$: Observable<T>) => Observable<T> {
  return (source$: Observable<T>) =>
    source$.pipe(
      catchError(({ status }: HttpErrorResponse) => {
        return status === HttpStatusCode.BadRequest ? of(fallbackValue) : EMPTY;
      }),
    );
}
